import { useRef } from "react";
import { toast } from "react-hot-toast";
import { TriangleAlert } from "lucide-react";

import {
  buildIbRedirectUrl,
  withIbReturnUrl,
} from "features/healthlink/utils/ibRedirect";
import { PENDING_IB_LAB_ORDER_KEY } from "features/labOrders/constants";
import { useDownloadLabOrder } from "features/labOrders/hooks/useDownloadLabOrder";

// The referral flow's Healthlink hand-over screen — the same component, because it is the
// same Integrated Browser and now the same journey out to it.
import { AdditionalInformationsForm } from "../../../PatientReferralsPage/components/AddReferralForm/components/AdditionalInformationsForm/AdditionalInformationsForm";
import s from "./LabOrderIbHandoff.module.css";

export const LabOrderIbHandoff = ({ submission, onFinished, isFlush = false }) => {
  const { mutateAsync: downloadLabOrder } = useDownloadLabOrder();
  const hasAnnouncedRef = useRef(false);

  const labOrderId = submission?.id;

  // Healthlink holds the template but the order row could not be saved — the GP can still
  // place the order, there is just nothing to track it with afterwards.
  const isUntracked = !labOrderId;

  const announceCompleted = () => {
    if (hasAnnouncedRef.current) return;

    hasAnnouncedRef.current = true;
    toast.success("Lab order submitted to Healthlink successfully!");
    onFinished();
  };

  // The referral journey, exactly: this tab navigates to Healthlink through our own
  // CloudFront redirect, which rewrites to the target and answers 302. Going straight to
  // Healthlink from our origin is what answered 403 — the redirect is what fixes the
  // session and CORS problems behind it (Dalen, #elab-ordering 2026-08-07).
  //
  // Healthlink is asked to come back to this page with the order id, and the id is also
  // stashed in sessionStorage, because Healthlink drops our params on the way back and
  // returns only its own ?controlId.
  const handleOpenInHealthlink = () => {
    if (!submission?.integratedBrowserUrl) return;

    const returnUrl = new URL(window.location.href);

    if (labOrderId) {
      returnUrl.searchParams.set("labOrderId", String(labOrderId));
      sessionStorage.setItem(PENDING_IB_LAB_ORDER_KEY, String(labOrderId));
    }

    window.location.href = buildIbRedirectUrl(
      withIbReturnUrl(submission.integratedBrowserUrl, returnUrl.toString())
    );
  };

  // The manual path, for a GP who took the copy-link route or came back by hand.
  const handleFinish = async () => {
    try {
      const result = await downloadLabOrder(labOrderId);

      if (result?.downloaded) {
        announceCompleted();

        return true;
      }

      toast.error(
        "Healthlink hasn't got the completed order yet. Make sure you submitted the form, then try again in a few seconds."
      );

      return false;
    } catch (error) {
      console.error("Failed to check the completed lab order", error);
      toast.error(
        "We couldn't check the order with Healthlink. Wait a few seconds and try again."
      );

      return false;
    }
  };

  return (
    <div className={s.handoff}>
      {!!isUntracked && (
        <div className={s.warning} role="alert">
          <TriangleAlert size={20} className={s.warningIcon} />
          <p className={s.warningText}>
            You can still complete this order in Healthlink, but it won&apos;t appear in
            this patient&apos;s list. Note the order reference:{" "}
            <strong>{submission?.messageControlId}</strong>
          </p>
        </div>
      )}

      <AdditionalInformationsForm
        link={submission?.integratedBrowserUrl}
        isFlush={isFlush}
        title="Complete this lab order in Healthlink"
        subtitle="Choose the tests, clinical details and specimen type on Healthlink to finish this order."
        confirmationLabel="I have finished this lab order in Healthlink."
        redirectLabel="Continue on Healthlink"
        redirectNote="Takes you to Healthlink and brings you back here when the order is submitted."
        instruction={
          <>
            <strong>Continue on Healthlink</strong> takes you there and returns you to this
            page when the order is submitted, where it is confirmed automatically.
            Alternatively, <strong>copy the link</strong> above into a new tab, complete the
            order there, then come back and select <strong>Finish</strong>.
          </>
        }
        onAutoRedirect={handleOpenInHealthlink}
        onFinish={handleFinish}
        onContinue={onFinished}
        hideFinishButton={isUntracked}
      />
    </div>
  );
};
